import React, { FC, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Button, Modal, Paper, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@mui/styles';

import StoreModel from 'models/Index';
import { history, RoutePaths } from 'helpers';

interface CashCollectionModalPromtProps {
    store: StoreModel;
}
interface CashCollectionModalPromtPropsInjected
    extends CashCollectionModalPromtProps {}

const useStyles = makeStyles(() => {
    return {
        modal: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
        },
        root: {
            width: '750px',
            padding: '56px',
            height: '400px',
            margin: 'auto',
            display: 'flex',
            flexDirection: 'column',
            position: 'absolute',

            borderRadius: '10px',
            border: '2px solid #009ADE',
        },
        header: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
        },
        body: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            padding: '20px 0',
        },
        button: {
            width: '150px',
        },
        footer: {
            display: 'flex',
            gap: '100px',
            justifyContent: 'center',
        },
    };
});

const CashCollectionModalPromt: FC<CashCollectionModalPromtProps> = (props) => {
    const {
        store: { peripheryStatus },
    } = props as CashCollectionModalPromtPropsInjected;

    const classes = useStyles();

    const [isOpen, setIsOpen] = useState(false);
    const [isCloseRequested, setIsCloseRequested] = useState(false);

    const { t } = useTranslation();

    useEffect(() => {
        const disabledScreens = [
            RoutePaths.CashCollection,
            RoutePaths.CashCollectionSummary,
            RoutePaths.CashCollectionSuccess,
        ];
        const isDisableInCurrentScreen = disabledScreens.includes(
            history.location.pathname
        );
        if (!peripheryStatus.isCassetteInserted && !isDisableInCurrentScreen) {
            setIsOpen(true);
        }
    }, [peripheryStatus.isCassetteInserted]);

    const waitForCassetteInserted = async () => {
        return new Promise<void>((resolve) => {
            const checkInterval = setInterval(() => {
                if (peripheryStatus.isCassetteInserted) {
                    clearInterval(checkInterval);
                    resolve();
                }
            }, 500);
        });
    };

    return (
        <Modal className={classes.modal} open={isOpen}>
            <Paper className={classes.root}>
                <div className={classes.header}>
                    <Typography variant='h3'>
                        {t('Cash Cassette Removed')}
                    </Typography>
                </div>
                <div className={classes.body}>
                    <Typography
                        align='center'
                        variant='h4'
                        style={{ fontWeight: 'normal' }}
                    >
                        {isCloseRequested
                            ? t(
                                  'Please, insert the cash cassette back into the Cash Acceptor'
                              )
                            : t(
                                  'Would you like to initiate the Cash Collection?'
                              )}
                    </Typography>
                </div>

                {!isCloseRequested && (
                    <div className={classes.footer}>
                        <Button
                            className={classes.button}
                            variant='secondary'
                            onClick={async () => {
                                if (peripheryStatus.isCassetteInserted) {
                                    setIsOpen(false);
                                    return;
                                }

                                setIsCloseRequested(true);
                                await waitForCassetteInserted();
                                setIsOpen(false);
                                setIsCloseRequested(false);
                            }}
                        >
                            <Typography variant='button'>{t('No')}</Typography>
                        </Button>
                        <Button
                            className={classes.button}
                            variant='primary'
                            onClick={() => {
                                history.push({
                                    pathname: RoutePaths.CashCollection,
                                    search: history.location.search,
                                });

                                setIsOpen(false);
                            }}
                        >
                            <Typography variant='button'>{t('Yes')}</Typography>
                        </Button>
                    </div>
                )}
            </Paper>
        </Modal>
    );
};

export default observer(CashCollectionModalPromt);
